import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtUGX } from "@/lib/format";
import { useEffectiveBrand } from "@/lib/tenant-branding";
import { CardSwipe } from "@/components/card-swipe";
import { cn } from "@/lib/utils";
import {
  Search, LayoutGrid, List, Star, Trash2, Plus, Minus, ShoppingCart, Wallet, CreditCard,
  Smartphone, Banknote, Ticket, Percent, StickyNote, PauseCircle, PlayCircle, XCircle,
  ShieldCheck, CheckCircle2, Printer, Receipt, Barcode, ScanLine, User, Clock, RotateCcw,
  Keyboard, ChevronRight, Sparkles, Utensils, Coffee, BookOpen, Shirt, Package, TrendingUp,
} from "lucide-react";

/* -------------------------------------------------------------------------- */
/*  Types                                                                     */
/* -------------------------------------------------------------------------- */

type StockItem = { id: string; merchant_id: string; name: string; price_ugx: number; quantity: number };
type CartItem = {
  id: string; name: string; price_ugx: number; qty: number; max: number;
  discountPct: number; note?: string;
};
type TenderKind = "wallet" | "cash" | "card" | "momo" | "voucher";
type Tender = { kind: TenderKind; amount: number; ref?: string };
type ParkedOrder = { id: string; label: string; cart: CartItem[]; note: string; discount: number; ts: number };

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const CATEGORY_RULES: { key: string; label: string; match: RegExp }[] = [
  { key: "food",     label: "Food",     match: /chapati|rolex|posho|pilau|plate|rice|beans|meal|meat|chicken|pie|mandaz|samo|snack/i },
  { key: "drinks",   label: "Drinks",   match: /juice|water|soda|coffee|tea|milk|drink|bottle/i },
  { key: "books",    label: "Books",    match: /book|novel|notebook|exercise|paper|set|pen|pencil|math/i },
  { key: "uniform",  label: "Uniform",  match: /uniform|shirt|tie|shoe|sock|blazer|skirt/i },
];
function categorize(name: string): string {
  for (const r of CATEGORY_RULES) if (r.match.test(name)) return r.key;
  return "other";
}
const CATEGORY_META: Record<string, { Icon: any; label: string; tint: string; accent: string; ink: string }> = {
  food:    { Icon: Utensils, label: "Food",    tint: "linear-gradient(135deg,#fff7ed,#fed7aa)", accent: "#f97316", ink: "#7c2d12" },
  drinks:  { Icon: Coffee,   label: "Drinks",  tint: "linear-gradient(135deg,#ecfeff,#bae6fd)", accent: "#0ea5e9", ink: "#075985" },
  books:   { Icon: BookOpen, label: "Books",   tint: "linear-gradient(135deg,#f5f3ff,#ddd6fe)", accent: "#8b5cf6", ink: "#4c1d95" },
  uniform: { Icon: Shirt,    label: "Uniform", tint: "linear-gradient(135deg,#fff1f2,#fecdd3)", accent: "#e11d48", ink: "#881337" },
  other:   { Icon: Package,  label: "General", tint: "linear-gradient(135deg,#f8fafc,#e2e8f0)", accent: "#64748b", ink: "#0f172a" },
};
function metaFor(name: string) {
  return CATEGORY_META[categorize(name)] ?? CATEGORY_META.other;
}
function paletteFor(name: string): string {
  // legacy accent gradient — kept for cart line items and rows that want a bold chip
  return metaFor(name).tint;
}
function useLocalStorage<T>(key: string, initial: T): [T, (v: T | ((p: T) => T)) => void] {
  const [v, setV] = useState<T>(() => {
    try { const raw = localStorage.getItem(key); return raw ? (JSON.parse(raw) as T) : initial; } catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(v)); } catch { /* ignore */ } }, [key, v]);
  return [v, setV];
}

const TENDER_META: Record<TenderKind, { label: string; icon: any; hint: string }> = {
  wallet:  { label: "Student wallet", icon: Wallet,     hint: "Scan QR or NFC" },
  cash:    { label: "Cash",           icon: Banknote,   hint: "Enter tendered" },
  card:    { label: "Card",           icon: CreditCard, hint: "Insert / tap"    },
  momo:    { label: "Mobile Money",   icon: Smartphone, hint: "MTN · Airtel"   },
  voucher: { label: "Voucher",        icon: Ticket,     hint: "Enter code"      },
};

/* -------------------------------------------------------------------------- */
/*  Main POS                                                                  */
/* -------------------------------------------------------------------------- */

export function POSCheckout({
  merchantId, stock, feeBps, onSale,
}: {
  merchantId: string; stock: StockItem[]; feeBps: number; onSale: () => void;
}) {
  const { brand } = useEffectiveBrand();
  const p = brand.primaryColor;
  const a = brand.accentColor;

  const [view, setView]         = useState<"grid" | "list">("grid");
  const [category, setCategory] = useState<string>("all");
  const [search, setSearch]     = useState("");
  const [favorites, setFavorites] = useLocalStorage<string[]>(`pos-favs-${merchantId}`, []);
  const [cart, setCart]         = useState<CartItem[]>([]);
  const [orderDiscount, setOrderDiscount] = useState(0);
  const [orderNote, setOrderNote] = useState("");
  const [taxPct, setTaxPct]     = useState(0);
  const [parked, setParked]     = useLocalStorage<ParkedOrder[]>(`pos-parked-${merchantId}`, []);
  const [tenderOpen, setTenderOpen] = useState(false);
  const [receipt, setReceipt]   = useState<ReceiptData | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  /* ---------------- keyboard shortcuts ---------------- */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement && ["INPUT","TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "/") { e.preventDefault(); searchRef.current?.focus(); }
      else if (e.key.toLowerCase() === "g") setView("grid");
      else if (e.key.toLowerCase() === "l") setView("list");
      else if (e.key.toLowerCase() === "p") parkCurrent();
      else if (e.key.toLowerCase() === "c" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); clearCart(); }
      else if (e.key === "Enter" && cart.length > 0) setTenderOpen(true);
      else if (e.key === "?") setShowShortcuts(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  /* ---------------- categories & filtering ---------------- */
  const categoryList = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of stock) {
      const c = categorize(s.name);
      counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    return CATEGORY_RULES
      .filter(r => (counts.get(r.key) ?? 0) > 0)
      .map(r => ({ key: r.key, label: r.label, count: counts.get(r.key)! }))
      .concat(counts.get("other") ? [{ key: "other", label: "Other", count: counts.get("other")! }] : []);
  }, [stock]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return stock.filter(s => {
      if (q && !s.name.toLowerCase().includes(q) && !s.id.toLowerCase().includes(q)) return false;
      if (category === "all") return true;
      if (category === "favorites") return favorites.includes(s.id);
      if (category === "low") return s.quantity > 0 && s.quantity < 10;
      return categorize(s.name) === category;
    });
  }, [stock, search, category, favorites]);

  /* ---------------- cart math ---------------- */
  const subtotal = cart.reduce((a, c) => a + c.price_ugx * c.qty, 0);
  const itemDiscount = cart.reduce((a, c) => a + Math.round(c.price_ugx * c.qty * (c.discountPct / 100)), 0);
  const preTaxOrder = Math.max(0, subtotal - itemDiscount);
  const orderDiscountAmt = Math.round(preTaxOrder * (orderDiscount / 100));
  const taxable = Math.max(0, preTaxOrder - orderDiscountAmt);
  const tax = Math.round(taxable * (taxPct / 100));
  const total = taxable + tax;
  const fee = Math.round((total * feeBps) / 10000);

  /* ---------------- cart mutators ---------------- */
  const add = (s: StockItem) => {
    setCart(c => {
      const ex = c.find(x => x.id === s.id);
      if (ex) return c.map(x => x.id === s.id ? { ...x, qty: Math.min(x.qty + 1, x.max) } : x);
      return [...c, { id: s.id, name: s.name, price_ugx: s.price_ugx, qty: 1, max: s.quantity, discountPct: 0 }];
    });
  };
  const setQty = (id: string, qty: number) =>
    setCart(c => c.map(x => x.id === id ? { ...x, qty: Math.max(1, Math.min(qty, x.max)) } : x));
  const setLineDiscount = (id: string, pct: number) =>
    setCart(c => c.map(x => x.id === id ? { ...x, discountPct: Math.max(0, Math.min(100, pct)) } : x));
  const setLineNote = (id: string, note: string) =>
    setCart(c => c.map(x => x.id === id ? { ...x, note } : x));
  const remove = (id: string) => setCart(c => c.filter(x => x.id !== id));
  const clearCart = () => { setCart([]); setOrderDiscount(0); setOrderNote(""); };

  const toggleFav = (id: string) =>
    setFavorites(f => f.includes(id) ? f.filter(x => x !== id) : [...f, id]);

  /* ---------------- park / resume ---------------- */
  const parkCurrent = () => {
    if (cart.length === 0) return;
    const label = orderNote.trim() || `#${(parked.length + 1).toString().padStart(3, "0")}`;
    setParked(p => [...p, { id: crypto.randomUUID(), label, cart, note: orderNote, discount: orderDiscount, ts: Date.now() }]);
    clearCart();
    toast.success(`Order parked · ${label}`);
  };
  const resumeParked = (o: ParkedOrder) => {
    if (cart.length > 0) parkCurrent();
    setCart(o.cart); setOrderNote(o.note); setOrderDiscount(o.discount);
    setParked(p => p.filter(x => x.id !== o.id));
    toast.info(`Resumed ${o.label}`);
  };

  /* ---------------- checkout ---------------- */
  const checkout = useMutation({
    mutationFn: async (tenders: Tender[]) => {
      if (cart.length === 0) throw new Error("Cart is empty");
      const walletTender = tenders.find(t => t.kind === "wallet");
      const walletId = walletTender?.ref?.trim();

      if (walletTender && walletId) {
        // Real backend charge via existing RPC
        let qr = walletId;
        if (!/^SOS-/i.test(walletId)) {
          const { data: card, error: ec } = await supabase.from("student_cards")
            .select("qr_token").eq("wallet_id", walletId).maybeSingle();
          if (ec || !card?.qr_token) throw new Error("No card linked to that wallet");
          qr = card.qr_token;
        }
        const { data, error } = await (supabase.rpc as any)("pos_checkout_by_qr", {
          _merchant_id: merchantId,
          _qr_token: qr,
          _items: cart.map(c => ({ stock_id: c.id, qty: c.qty })),
        });
        if (error) throw new Error(error.message);
        return data as { total_ugx: number; wallet_balance_ugx: number; sale_id: string };
      }
      // Non-wallet — offline sale, queued for sync
      return { total_ugx: total, wallet_balance_ugx: 0, sale_id: "OFFLINE-" + crypto.randomUUID().slice(0, 8) };
    },
    onSuccess: (_res, tenders) => {
      const change = Math.max(0, tenders.reduce((a, t) => a + t.amount, 0) - total);
      setReceipt({
        id: crypto.randomUUID(),
        ts: new Date(),
        items: cart, tenders, subtotal, itemDiscount, orderDiscountAmt, tax, total, change,
        note: orderNote,
      });
      setTenderOpen(false);
      onSale();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const finishReceipt = () => { setReceipt(null); clearCart(); };

  /* -------------------------------------------------------------------- */
  /*  Render                                                              */
  /* -------------------------------------------------------------------- */
  return (
    <div className="relative -mx-4 -my-6 h-[calc(100vh-8rem)] overflow-hidden sm:-mx-6">
      {/* Ambient brand glow */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-70">
        <div className="absolute -top-24 left-1/3 h-96 w-96 rounded-full blur-3xl" style={{ background: `${p}22` }} />
        <div className="absolute bottom-0 right-0 h-96 w-96 rounded-full blur-3xl" style={{ background: `${a}22` }} />
      </div>

      <div className="grid h-full grid-cols-[220px_minmax(0,1fr)_400px] gap-0">
        {/* =============== CATEGORY RAIL =============== */}
        <aside className="flex h-full flex-col border-r bg-card/70 backdrop-blur">
          <div className="border-b px-4 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Register</div>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
              <span className="text-sm font-semibold">Till 01 · Live</span>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto p-2">
            {[
              { key: "all", label: "All items", icon: LayoutGrid, count: stock.length },
              { key: "favorites", label: "Favorites", icon: Star, count: favorites.length },
              { key: "low", label: "Low stock", icon: Sparkles, count: stock.filter(s => s.quantity > 0 && s.quantity < 10).length },
            ].map(c => (
              <CategoryBtn key={c.key} active={category === c.key} onClick={() => setCategory(c.key)} icon={c.icon} label={c.label} count={c.count} accent={p} />
            ))}
            <div className="mt-3 mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Categories</div>
            {categoryList.map(c => (
              <CategoryBtn key={c.key} active={category === c.key} onClick={() => setCategory(c.key)} icon={LayoutGrid} label={c.label} count={c.count} accent={p} />
            ))}
          </nav>

          {/* Parked orders */}
          <div className="border-t bg-muted/30 p-2">
            <div className="mb-1 flex items-center justify-between px-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Parked ({parked.length})</div>
              <button onClick={() => setShowShortcuts(true)} title="Keyboard shortcuts" className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
                <Keyboard className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {parked.length === 0 && <div className="rounded-md px-2 py-1.5 text-[11px] text-muted-foreground">No parked orders</div>}
              {parked.map(o => (
                <button key={o.id} onClick={() => resumeParked(o)}
                  className="group flex w-full items-center justify-between rounded-md border bg-card px-2 py-1.5 text-left text-xs transition hover:border-primary">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{o.label}</div>
                    <div className="text-[10px] text-muted-foreground">{o.cart.length} items · {new Date(o.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <PlayCircle className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" />
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* =============== CATALOG =============== */}
        <section className="flex h-full flex-col">
          {/* Search toolbar */}
          <div className="flex items-center gap-2 border-b bg-card/60 px-4 py-2.5 backdrop-blur">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products, SKU, or scan barcode…  ( / to focus )"
                className="h-10 pl-9 pr-24 text-sm"
              />
              <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-muted-foreground" title="Barcode scan (paste SKU)">
                  <Barcode className="mr-1 h-3.5 w-3.5" />Scan
                </Button>
              </div>
            </div>
            <div className="flex overflow-hidden rounded-md border">
              <button onClick={() => setView("grid")} className={cn("px-2.5 py-1.5 transition", view === "grid" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted")} title="Grid (G)">
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button onClick={() => setView("list")} className={cn("px-2.5 py-1.5 transition", view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted")} title="List (L)">
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Grid / list */}
          <div className="flex-1 overflow-y-auto px-4 pb-6 pt-4">
            {filtered.length === 0 && (
              <div className="grid h-full place-items-center text-center">
                <div>
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-muted"><ScanLine className="h-6 w-6 text-muted-foreground" /></div>
                  <div className="mt-3 text-sm font-medium">No items match</div>
                  <div className="text-xs text-muted-foreground">Try clearing the search or category.</div>
                </div>
              </div>
            )}

            {view === "grid" ? (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-4">
                {filtered.map(s => (
                  <ProductCard key={s.id} item={s}
                    inCart={cart.find(c => c.id === s.id)?.qty ?? 0}
                    isFav={favorites.includes(s.id)}
                    onAdd={() => add(s)}
                    onFav={() => toggleFav(s.id)}
                  />
                ))}
              </div>
            ) : (
              <div className="divide-y rounded-xl border bg-card">
                {filtered.map(s => (
                  <ProductRow key={s.id} item={s}
                    inCart={cart.find(c => c.id === s.id)?.qty ?? 0}
                    isFav={favorites.includes(s.id)}
                    onAdd={() => add(s)}
                    onFav={() => toggleFav(s.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* =============== CART / TICKET =============== */}
        <aside className="flex h-full flex-col border-l bg-card/80 backdrop-blur">
          {/* Ticket header */}
          <div className="flex items-center justify-between border-b px-4 py-3">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Current sale</div>
              <div className="text-sm font-semibold">{cart.length} item{cart.length === 1 ? "" : "s"}</div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={parkCurrent} disabled={cart.length === 0} title="Park (P)">
                <PauseCircle className="mr-1 h-3.5 w-3.5" />Park
              </Button>
              <Button size="sm" variant="ghost" onClick={clearCart} disabled={cart.length === 0} title="Clear (⌘C)">
                <XCircle className="mr-1 h-3.5 w-3.5" />Clear
              </Button>
            </div>
          </div>

          {/* Customer strip */}
          <div className="border-b bg-muted/30 px-4 py-2">
            <div className="flex items-center gap-2 text-xs">
              <div className="grid h-8 w-8 place-items-center rounded-full bg-primary/10 text-primary">
                <User className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium">Walk-in customer</div>
                <div className="text-[10px] text-muted-foreground">Add on checkout for loyalty & digital receipt</div>
              </div>
            </div>
          </div>

          {/* Line items */}
          <div className="flex-1 overflow-y-auto px-3 py-3">
            {cart.length === 0 && (
              <div className="grid h-full place-items-center text-center">
                <div>
                  <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-muted">
                    <ShoppingCart className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="mt-3 text-sm font-medium">Ticket is empty</div>
                  <div className="mt-1 text-xs text-muted-foreground">Tap items to add them.</div>
                  <div className="mt-4 text-[10px] uppercase tracking-wider text-muted-foreground">Press <kbd className="rounded border bg-muted px-1">/</kbd> to search</div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              {cart.map(c => (
                <LineItem key={c.id} item={c}
                  onQty={(q) => setQty(c.id, q)}
                  onDiscount={(pct) => setLineDiscount(c.id, pct)}
                  onNote={(n) => setLineNote(c.id, n)}
                  onRemove={() => remove(c.id)}
                />
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t bg-card/60 px-4 py-3">
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-1 rounded-lg border bg-card px-2 py-1.5 text-xs">
                <Percent className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Disc</span>
                <input type="number" min={0} max={100} value={orderDiscount}
                  onChange={(e) => setOrderDiscount(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  className="w-full bg-transparent text-right font-mono outline-none" />
                <span className="text-muted-foreground">%</span>
              </label>
              <label className="flex items-center gap-1 rounded-lg border bg-card px-2 py-1.5 text-xs">
                <Receipt className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Tax</span>
                <input type="number" min={0} max={50} value={taxPct}
                  onChange={(e) => setTaxPct(Math.max(0, Math.min(50, Number(e.target.value) || 0)))}
                  className="w-full bg-transparent text-right font-mono outline-none" />
                <span className="text-muted-foreground">%</span>
              </label>
            </div>

            <div className="mt-2">
              <div className="flex items-center gap-1 rounded-lg border bg-card px-2 py-1.5 text-xs">
                <StickyNote className="h-3 w-3 text-muted-foreground" />
                <input value={orderNote} onChange={(e) => setOrderNote(e.target.value)}
                  placeholder="Order note (optional)"
                  className="w-full bg-transparent outline-none placeholder:text-muted-foreground" />
              </div>
            </div>

            <div className="mt-3 space-y-1 text-xs">
              <Row label="Subtotal" value={fmtUGX(subtotal)} />
              {itemDiscount > 0 && <Row label="Item discounts" value={`− ${fmtUGX(itemDiscount)}`} muted />}
              {orderDiscountAmt > 0 && <Row label={`Order discount (${orderDiscount}%)`} value={`− ${fmtUGX(orderDiscountAmt)}`} muted />}
              {tax > 0 && <Row label={`Tax (${taxPct}%)`} value={fmtUGX(tax)} muted />}
              <div className="flex items-baseline justify-between pt-1">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Total due</span>
                <span className="font-display text-2xl font-semibold tabular-nums">{fmtUGX(total)}</span>
              </div>
              <div className="text-right text-[10px] text-muted-foreground">Merchant fee {fmtUGX(fee)} · {(feeBps / 100).toFixed(2)}%</div>
            </div>

            <Button
              onClick={() => setTenderOpen(true)}
              disabled={cart.length === 0}
              className="mt-3 h-12 w-full text-base font-semibold shadow-lg"
              style={{ background: `linear-gradient(120deg, ${p}, ${a})`, color: "white", boxShadow: `0 10px 30px -12px ${p}` }}
            >
              <ShoppingCart className="mr-2 h-4 w-4" /> Charge {fmtUGX(total)}
              <ChevronRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </aside>
      </div>

      {/* Tender dialog */}
      <TenderDialog
        open={tenderOpen} onOpenChange={setTenderOpen}
        total={total} onConfirm={(tenders) => checkout.mutate(tenders)}
        pending={checkout.isPending}
        primary={p} accent={a}
      />

      {/* Receipt preview */}
      {receipt && (
        <ReceiptOverlay
          receipt={receipt} brandName={brand.name} logoUrl={brand.logoUrl}
          primary={p} accent={a} feeBps={feeBps}
          onDone={finishReceipt}
        />
      )}

      {/* Shortcuts */}
      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Keyboard shortcuts</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            {[
              ["/", "Focus search"], ["G / L", "Grid / list view"], ["P", "Park current sale"],
              ["Enter", "Open tender"], ["⌘/Ctrl + C", "Clear ticket"], ["?", "Show shortcuts"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-1.5">
                <span className="text-muted-foreground">{v}</span>
                <kbd className="rounded border bg-card px-2 py-0.5 font-mono text-xs">{k}</kbd>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                            */
/* -------------------------------------------------------------------------- */

function CategoryBtn({ active, onClick, icon: Icon, label, count, accent }: any) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm transition",
        active ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground/80 hover:bg-muted"
      )}
      style={active ? { background: accent } : undefined}
    >
      <Icon className={cn("h-4 w-4 shrink-0", active ? "text-white" : "text-muted-foreground group-hover:text-foreground")} />
      <span className="flex-1 truncate text-left">{label}</span>
      <span className={cn("rounded-full px-1.5 text-[10px]", active ? "bg-white/20 text-white" : "bg-muted text-muted-foreground")}>{count}</span>
    </button>
  );
}

function ProductCard({ item, inCart, isFav, onAdd, onFav }: {
  item: StockItem; inCart: number; isFav: boolean; onAdd: () => void; onFav: () => void;
}) {
  const out = item.quantity === 0;
  const low = !out && item.quantity < 10;
  const meta = metaFor(item.name);
  const Icon = meta.Icon;
  return (
    <div
      className={cn(
        "group relative isolate flex flex-col overflow-hidden rounded-2xl border bg-card shadow-sm transition-all",
        out ? "opacity-60" : "hover:-translate-y-0.5 hover:shadow-xl hover:border-transparent",
      )}
      style={{ boxShadow: inCart > 0 ? `0 0 0 2px ${meta.accent} inset, 0 10px 30px -12px ${meta.accent}55` : undefined }}
    >
      {/* Favorite */}
      <button
        onClick={onFav}
        aria-label="Toggle favorite"
        className="absolute right-2 top-2 z-10 grid h-7 w-7 place-items-center rounded-full bg-white/90 text-muted-foreground shadow ring-1 ring-black/5 backdrop-blur transition hover:text-amber-500"
      >
        <Star className={cn("h-3.5 w-3.5", isFav && "fill-amber-400 text-amber-500")} />
      </button>

      <button
        onClick={onAdd}
        disabled={out}
        className="flex flex-1 flex-col text-left disabled:cursor-not-allowed"
      >
        {/* Illustrated header */}
        <div className="relative h-28 w-full overflow-hidden" style={{ background: meta.tint }}>
          {/* accent orb */}
          <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-40 blur-2xl" style={{ background: meta.accent }} />
          {/* big icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon className="h-14 w-14 transition-transform duration-300 group-hover:scale-110" style={{ color: meta.accent, strokeWidth: 1.6 }} />
          </div>
          {/* category chip */}
          <div className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-white/85 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shadow-sm backdrop-blur" style={{ color: meta.ink }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.accent }} />
            {meta.label}
          </div>
          {/* status overlays */}
          {out && (
            <div className="absolute inset-0 grid place-items-center bg-black/55 text-xs font-semibold uppercase tracking-widest text-white">
              Out of stock
            </div>
          )}
          {low && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow">
              <TrendingUp className="h-3 w-3" /> Low · {item.quantity}
            </div>
          )}
          {inCart > 0 && (
            <div className="absolute bottom-2 right-2 grid h-7 min-w-[28px] place-items-center rounded-full px-2 text-xs font-bold text-white shadow-lg" style={{ background: meta.accent }}>
              ×{inCart}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col gap-1.5 p-3">
          <div className="line-clamp-2 min-h-[2.5rem] text-[13px] font-semibold leading-tight text-foreground">
            {item.name}
          </div>
          <div className="mt-auto flex items-end justify-between gap-2">
            <div>
              <div className="font-mono text-base font-bold leading-none tabular-nums" style={{ color: meta.ink }}>
                {fmtUGX(item.price_ugx)}
              </div>
              {!out && (
                <div className="mt-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  {item.quantity} in stock
                </div>
              )}
            </div>
            <span
              className="grid h-8 w-8 place-items-center rounded-full text-white shadow-md transition-transform group-hover:scale-110"
              style={{ background: meta.accent }}
              aria-hidden
            >
              <Plus className="h-4 w-4" strokeWidth={2.6} />
            </span>
          </div>
        </div>
      </button>
    </div>
  );
}

function ProductRow({ item, inCart, isFav, onAdd, onFav }: {
  item: StockItem; inCart: number; isFav: boolean; onAdd: () => void; onFav: () => void;
}) {
  const out = item.quantity === 0;
  const low = !out && item.quantity < 10;
  const meta = metaFor(item.name);
  const Icon = meta.Icon;
  return (
    <div className={cn("group flex items-center gap-3 px-3 py-2.5 transition hover:bg-muted/50", out && "opacity-60")}>
      <div className="relative grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-xl" style={{ background: meta.tint }}>
        <Icon className="h-5 w-5" style={{ color: meta.accent }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-[9px] font-semibold uppercase tracking-wider" style={{ color: meta.accent }}>{meta.label}</span>
          {low && <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">Low · {item.quantity}</span>}
          {out && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-bold uppercase text-muted-foreground">Out</span>}
        </div>
        <div className="truncate text-sm font-semibold">{item.name}</div>
        {!out && <div className="text-[11px] text-muted-foreground">{item.quantity} in stock</div>}
      </div>
      <div className="w-24 text-right">
        <div className="font-mono text-sm font-bold tabular-nums" style={{ color: meta.ink }}>{fmtUGX(item.price_ugx)}</div>
      </div>
      <button onClick={onFav} className="p-1 text-muted-foreground hover:text-amber-500">
        <Star className={cn("h-4 w-4", isFav && "fill-amber-400 text-amber-500")} />
      </button>
      <Button size="sm" disabled={out} onClick={onAdd} className="text-white shadow-sm" style={{ background: meta.accent }}>
        {inCart > 0 ? <><Plus className="mr-1 h-3 w-3" />{inCart}</> : "Add"}
      </Button>
    </div>
  );
}

function LineItem({ item, onQty, onDiscount, onNote, onRemove }: {
  item: CartItem; onQty: (n: number) => void; onDiscount: (n: number) => void;
  onNote: (n: string) => void; onRemove: () => void;
}) {
  const [expand, setExpand] = useState(false);
  const lineTotal = item.price_ugx * item.qty;
  const discAmt = Math.round(lineTotal * (item.discountPct / 100));
  return (
    <div className="rounded-xl border bg-card p-2.5">
      <div className="flex items-start gap-2">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg" style={{ background: metaFor(item.name).tint }}>
          {(() => { const I = metaFor(item.name).Icon; return <I className="h-4 w-4" style={{ color: metaFor(item.name).accent }} />; })()}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <div className="truncate text-sm font-medium">{item.name}</div>
            <div className="shrink-0 font-mono text-sm tabular-nums">{fmtUGX(lineTotal - discAmt)}</div>
          </div>
          <div className="mt-1 flex items-center justify-between">
            <div className="flex items-center gap-1 rounded-full border bg-background">
              <button onClick={() => onQty(item.qty - 1)} className="grid h-6 w-6 place-items-center rounded-full text-muted-foreground hover:text-foreground">
                <Minus className="h-3 w-3" />
              </button>
              <span className="w-6 text-center text-xs font-semibold tabular-nums">{item.qty}</span>
              <button onClick={() => onQty(item.qty + 1)} className="grid h-6 w-6 place-items-center rounded-full text-muted-foreground hover:text-foreground">
                <Plus className="h-3 w-3" />
              </button>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setExpand(x => !x)} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" title="Line options">
                <StickyNote className="h-3.5 w-3.5" />
              </button>
              <button onClick={onRemove} className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      {expand && (
        <div className="mt-2 grid grid-cols-[1fr_auto] gap-2 border-t pt-2 text-xs">
          <input value={item.note ?? ""} onChange={(e) => onNote(e.target.value)}
            placeholder="Note (no onion, extra sauce…)" className="w-full rounded-md border bg-background px-2 py-1 outline-none" />
          <label className="flex items-center gap-1 rounded-md border bg-background px-2 py-1">
            <Percent className="h-3 w-3 text-muted-foreground" />
            <input type="number" min={0} max={100} value={item.discountPct}
              onChange={(e) => onDiscount(Number(e.target.value) || 0)}
              className="w-10 bg-transparent text-right outline-none" />
          </label>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className={cn("flex items-center justify-between", muted && "text-muted-foreground")}>
      <span>{label}</span><span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Tender dialog — split payments                                            */
/* -------------------------------------------------------------------------- */

function TenderDialog({
  open, onOpenChange, total, onConfirm, pending, primary, accent,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; total: number;
  onConfirm: (tenders: Tender[]) => void; pending: boolean;
  primary: string; accent: string;
}) {
  const [tenders, setTenders] = useState<Tender[]>([]);
  const [kind, setKind] = useState<TenderKind>("wallet");
  const [amount, setAmount] = useState<string>("");
  const [ref, setRef] = useState("");

  useEffect(() => {
    if (open) {
      setTenders([]); setKind("wallet"); setAmount(""); setRef("");
    }
  }, [open]);

  const tendered = tenders.reduce((a, t) => a + t.amount, 0);
  const remaining = Math.max(0, total - tendered);
  const change = Math.max(0, tendered - total);
  const canConfirm = tendered >= total && (
    !tenders.some(t => t.kind === "wallet") || tenders.find(t => t.kind === "wallet")?.ref
  );

  const addTender = () => {
    const amt = Number(amount) || remaining;
    if (amt <= 0) return;
    if (kind === "wallet" && !ref.trim()) { toast.error("Scan the wallet QR or paste ID"); return; }
    if (kind === "voucher" && !ref.trim()) { toast.error("Voucher code required"); return; }
    setTenders(t => [...t, { kind, amount: amt, ref: ref.trim() || undefined }]);
    setAmount(""); setRef("");
  };

  const Icon = TENDER_META[kind].icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl overflow-hidden p-0">
        <div className="grid grid-cols-[1fr_320px]">
          {/* Left — method */}
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold">Take payment</DialogTitle>
            </DialogHeader>

            <div className="mt-4 grid grid-cols-5 gap-2">
              {(Object.keys(TENDER_META) as TenderKind[]).map(k => {
                const M = TENDER_META[k];
                const KIcon = M.icon;
                const active = kind === k;
                return (
                  <button key={k} onClick={() => setKind(k)}
                    className={cn("flex flex-col items-center gap-1 rounded-xl border py-3 text-xs transition",
                      active ? "border-transparent text-white shadow" : "bg-card text-foreground hover:border-primary")}
                    style={active ? { background: `linear-gradient(135deg, ${primary}, ${accent})` } : undefined}
                  >
                    <KIcon className="h-4 w-4" />
                    <span className="text-[10px] font-semibold uppercase tracking-wider">{M.label.split(" ")[0]}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-4 rounded-xl border bg-muted/40 p-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Icon className="h-3.5 w-3.5" />{TENDER_META[kind].hint}
              </div>

              {(kind === "wallet" || kind === "voucher") && (
                <div className="mt-2 flex gap-2">
                  <Input value={ref} onChange={(e) => setRef(e.target.value)}
                    placeholder={kind === "wallet" ? "Scan SOS-… or paste wallet ID" : "Voucher / promo code"} />
                  {kind === "wallet" && (
                    <CardSwipe onResolve={(w) => setRef(w.id)} trigger={<Button variant="outline" size="sm"><Smartphone className="mr-1 h-3 w-3" />Tap</Button>} />
                  )}
                </div>
              )}

              <div className="mt-2 grid grid-cols-[1fr_auto] gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Amount</Label>
                  <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={fmtUGX(remaining)} />
                </div>
                <div className="flex items-end">
                  <Button onClick={addTender} disabled={remaining === 0}>Add tender</Button>
                </div>
              </div>

              {kind === "cash" && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {[remaining, 5000, 10000, 20000, 50000].filter(n => n > 0).slice(0, 5).map((n, i) => (
                    <button key={i} onClick={() => setAmount(String(n))}
                      className="rounded-full border bg-card px-2.5 py-1 text-[11px] hover:border-primary">
                      {fmtUGX(n)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {tenders.length > 0 && (
              <div className="mt-3 space-y-1.5 text-sm">
                {tenders.map((t, i) => {
                  const M = TENDER_META[t.kind];
                  const TIcon = M.icon;
                  return (
                    <div key={i} className="flex items-center gap-2 rounded-lg border bg-card px-2.5 py-1.5">
                      <TIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs">{M.label}</span>
                      {t.ref && <span className="text-[10px] text-muted-foreground">· {t.ref.slice(0, 12)}</span>}
                      <span className="ml-auto font-mono">{fmtUGX(t.amount)}</span>
                      <button onClick={() => setTenders(x => x.filter((_, ix) => ix !== i))} className="text-muted-foreground hover:text-destructive">
                        <XCircle className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Right — summary */}
          <div className="border-l p-6" style={{ background: `linear-gradient(180deg, color-mix(in oklab, ${primary} 6%, transparent), transparent)` }}>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Total due</div>
            <div className="font-display text-4xl font-semibold tabular-nums">{fmtUGX(total)}</div>

            <div className="mt-5 space-y-1 text-sm">
              <Row label="Tendered" value={fmtUGX(tendered)} />
              <Row label="Remaining" value={fmtUGX(remaining)} muted />
              {change > 0 && (
                <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-emerald-800">
                  <div className="text-[10px] font-semibold uppercase tracking-widest">Change due</div>
                  <div className="font-display text-2xl font-semibold tabular-nums">{fmtUGX(change)}</div>
                </div>
              )}
            </div>

            <DialogFooter className="mt-5 flex flex-col gap-2 sm:flex-col">
              <Button
                onClick={() => onConfirm(tenders)}
                disabled={!canConfirm || pending}
                className="h-11 w-full text-base font-semibold"
                style={{ background: `linear-gradient(120deg, ${primary}, ${accent})`, color: "white" }}
              >
                {pending ? "Charging…" : <><ShieldCheck className="mr-2 h-4 w-4" />Complete sale</>}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">Cancel</Button>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  Receipt overlay                                                           */
/* -------------------------------------------------------------------------- */

type ReceiptData = {
  id: string; ts: Date; items: CartItem[]; tenders: Tender[];
  subtotal: number; itemDiscount: number; orderDiscountAmt: number; tax: number;
  total: number; change: number; note: string;
};

function ReceiptOverlay({
  receipt, brandName, logoUrl, primary, accent, feeBps, onDone,
}: {
  receipt: ReceiptData; brandName: string; logoUrl?: string | null;
  primary: string; accent: string; feeBps: number; onDone: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-[min(420px,100%)] overflow-hidden rounded-3xl border bg-card shadow-2xl">
        <div className="relative flex items-center gap-3 px-5 py-4 text-white" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}>
          {logoUrl ? (
            <img src={logoUrl} alt={brandName} className="h-10 w-10 rounded-lg object-cover ring-1 ring-white/25" />
          ) : (
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-white/15 text-sm font-bold ring-1 ring-white/25">
              {brandName.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-white/80">{brandName} · Point of Sale</div>
            <div className="text-sm font-semibold">Receipt</div>
          </div>
          <CheckCircle2 className="ml-auto h-6 w-6" />
        </div>

        <div className="px-5 pt-5 text-center">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Amount received</div>
          <div className="mt-1 font-display text-4xl font-semibold tabular-nums">{fmtUGX(receipt.total)}</div>
          <div className="mt-1 text-xs text-muted-foreground">
            {receipt.items.length} item{receipt.items.length === 1 ? "" : "s"} · {receipt.ts.toLocaleTimeString()} · {receipt.id.slice(0, 8).toUpperCase()}
          </div>
          {receipt.change > 0 && (
            <div className="mx-auto mt-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
              <RotateCcw className="h-3 w-3" />Change {fmtUGX(receipt.change)}
            </div>
          )}
        </div>

        <div className="mx-5 mt-4 rounded-xl border bg-muted/30 p-3 text-xs">
          {receipt.items.map(i => (
            <div key={i.id} className="flex justify-between py-0.5">
              <span className="truncate">{i.qty} × {i.name}</span>
              <span className="font-mono">{fmtUGX(i.qty * i.price_ugx * (1 - i.discountPct / 100))}</span>
            </div>
          ))}
          <div className="mt-2 border-t pt-2">
            <Row label="Subtotal" value={fmtUGX(receipt.subtotal)} muted />
            {receipt.itemDiscount > 0 && <Row label="Item discount" value={`− ${fmtUGX(receipt.itemDiscount)}`} muted />}
            {receipt.orderDiscountAmt > 0 && <Row label="Order discount" value={`− ${fmtUGX(receipt.orderDiscountAmt)}`} muted />}
            {receipt.tax > 0 && <Row label="Tax" value={fmtUGX(receipt.tax)} muted />}
            <div className="mt-1 flex justify-between font-semibold"><span>Total</span><span className="font-mono">{fmtUGX(receipt.total)}</span></div>
          </div>
          <div className="mt-2 border-t pt-2 text-[11px]">
            {receipt.tenders.map((t, i) => (
              <div key={i} className="flex justify-between">
                <span>{TENDER_META[t.kind].label}</span>
                <span className="font-mono">{fmtUGX(t.amount)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-1.5 pb-2 pt-3 text-[11px] text-emerald-600">
          <ShieldCheck className="h-3.5 w-3.5" /> Settled to wallet ledger · fee {fmtUGX(Math.round(receipt.total * feeBps / 10000))}
        </div>

        <div className="flex gap-2 border-t bg-muted/30 p-3">
          <Button variant="outline" size="sm" className="flex-1" onClick={() => window.print()}>
            <Printer className="mr-1.5 h-3.5 w-3.5" />Print
          </Button>
          <Button variant="outline" size="sm" className="flex-1" onClick={() => toast.success("Digital receipt sent")}>
            <Receipt className="mr-1.5 h-3.5 w-3.5" />Email / SMS
          </Button>
          <Button size="sm" className="flex-1" onClick={onDone}>
            <Clock className="mr-1.5 h-3.5 w-3.5" />New sale
          </Button>
        </div>
      </div>
    </div>
  );
}
